import { FC, ReactNode } from 'react'
import { LoadingSpinner } from '../atoms'
import {
  CocaineIcon,
  CocaineActiveIcon,
  CocaineLockedIcon,
  XanaxIcon,
  XanaxActiveIcon,
  XanaxLockedIcon,
  MdmaIcon,
  MdmaActiveIcon,
  MdmaLockedIcon,
  ModuleCompletedBackground,
  ModuleCurrentBackground,
  ModuleLockedBackground,
  ModuleLockedBackgroundLowRes,
  ModuleReviewedBackgroundCocaine,
  ModuleReviewedBackgroundCocaineLowRes,
  ModuleReviewedBackgroundMDMA,
  ModuleReviewedBackgroundMDMALowRes,
  ModuleReviewedBackgroundResponse,
  ModuleReviewedBackgroundResponseLowRes,
  ModuleReviewedBackgroundXanax,
  ModuleReviewedBackgroundXanaxLowRes,
  ResponseIcon,
  ResponseActiveIcon,
  ResponseLockedIcon
} from '../../assets/images'
import { Module } from '../../models/global.types'
import Text from '../atoms/Text/Text'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ProgressiveImage from '../molecules/ProgressiveImage/ProgressiveImage'

const images: Record<string, { icon: string, activeIcon: string, lockedIcon: string, reviewedBackground: string, reviewedBackgroundLowRes: string }> = {
  cocaine: {
    icon: CocaineIcon,
    activeIcon: CocaineActiveIcon,
    lockedIcon: CocaineLockedIcon,
    reviewedBackground: ModuleReviewedBackgroundCocaine,
    reviewedBackgroundLowRes: ModuleReviewedBackgroundCocaineLowRes
  },
  xanax: {
    icon: XanaxIcon,
    activeIcon: XanaxActiveIcon,
    lockedIcon: XanaxLockedIcon,
    reviewedBackground: ModuleReviewedBackgroundXanax,
    reviewedBackgroundLowRes: ModuleReviewedBackgroundXanaxLowRes
  },
  mdma: {
    icon: MdmaIcon,
    activeIcon: MdmaActiveIcon,
    lockedIcon: MdmaLockedIcon,
    reviewedBackground: ModuleReviewedBackgroundMDMA,
    reviewedBackgroundLowRes: ModuleReviewedBackgroundMDMALowRes
  },
  response: {
    icon: ResponseIcon,
    activeIcon: ResponseActiveIcon,
    lockedIcon: ResponseLockedIcon,
    reviewedBackground: ModuleReviewedBackgroundResponse,
    reviewedBackgroundLowRes: ModuleReviewedBackgroundResponseLowRes
  }
}

interface CourseTileProps {
  label: string
  loading?: boolean
  module: Module
  onClick: () => void
  reviewState: boolean
}

const CourseTile: FC<CourseTileProps> = ({
  onClick,
  label,
  loading,
  module,
  reviewState
}) => {
  const keyboardControls = useKeyboardControls()

  let labelColor = module.isCurrent ? 'text-dopage-primary' : 'text-dopage-lightGreen'
  let labelImage = images[module.name.toLowerCase()]?.icon
  let tileBackgroundImage = images[module.name.toLowerCase()]?.reviewedBackground
  let tileBackgroundImageLowRes = images[module.name.toLowerCase()]?.reviewedBackgroundLowRes
  let tileClassName = ''
  let buttonText = 'REPLAY'

  if (module.isLocked) {
    labelImage = images[module.name.toLowerCase()]?.lockedIcon
    buttonText = ''
    tileBackgroundImage = ModuleLockedBackground
    tileBackgroundImageLowRes = ModuleLockedBackgroundLowRes
  }

  if (module.isCompleted) {
    labelColor = 'text-black'
    tileBackgroundImage = ModuleCompletedBackground
  }

  if (module.isCurrent) {
    labelImage = images[module.name.toLowerCase()]?.activeIcon
    tileBackgroundImage = ModuleCurrentBackground
    tileClassName = 'glow-effect'
    buttonText = 'LAUNCH'
  }

  if (reviewState) {
    tileBackgroundImage = images[module.name.toLowerCase()]?.reviewedBackground
  }

  const idButton = `button-module-${module.id}`

  return (
    <MainContainer
      isButton={!module.isLocked || module.isCurrent}
      onClick={onClick}
      idButton={idButton}
    >
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButton)}
        highlightEffect={HighlightEffect.ButtonModule}
        data-testid={`highlight-${idButton}`}
      >
        <div className='relative h-[291px] w-[250px] flex flex-col'>
          <ProgressiveImage
            alt='background'
            loading={loading === true}
            loadingPlaceholderSrc={ModuleLockedBackgroundLowRes}
            loadingSrc={ModuleLockedBackground}
            placeholderSrc={tileBackgroundImageLowRes}
            src={tileBackgroundImage}
            className={`${tileClassName} h-full`}
            wrapperClassName='h-full'
          />
          <div className='absolute flex flex-col w-full h-full items-center justify-between'>
            <div className={`flex flex-col h-[20%] pt-3 text-center font-primary font-bold text-[26px] ${loading === true ? 'text-dopage-lightGreen' : labelColor}`}>
              <Text text={label.split(':')[0].toUpperCase()} />
              <Text text={module.isLocked && !module.isCurrent ? 'LOCKED' : label.split(':')[1]} size='LG' />
            </div>
            {loading === true ? <LoadingSpinner /> : <img src={labelImage} alt={`${label} icon`} className='h-[50%]' />}
            <div className='h-[12%]'>
              <Text
                className={` ${module.isCurrent ? 'text-black' : 'text-dopage-lightBlue'}`}
                text={loading === true ? 'LOADING' : buttonText}
                bold
              />
            </div>
          </div>
        </div>
      </ControlHighlight>
    </MainContainer>
  )
}

interface MainContainerProps {
  children: ReactNode
  isButton?: boolean
  onClick?: () => void
  idButton: string
}

const MainContainer: FC<MainContainerProps> = ({ children, isButton, onClick, idButton }: MainContainerProps) => {
  const containerClassName = 'accessible-element relative max-w-[326px] w-full mx-[10px] flex justify-center'

  if (isButton === true) {
    return (
      <div className={containerClassName}>
        <KeyboardControl>
          <button
            id={idButton}
            data-testid={idButton}
            onClick={onClick}
          >
            {children}
          </button>
        </KeyboardControl>
      </div>
    )
  } else {
    return (
      <div className={containerClassName}>
        {children}
      </div>
    )
  }
}

export default CourseTile
